import {
  DndContext,
  type DragEndEvent,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  horizontalListSortingStrategy,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import type { ReactNode } from 'react';

export function reorderForDragEnd<T extends { id: string }>(
  items: T[],
  activeId: string,
  overId: string,
): string[] {
  if (activeId === overId) return items.map((i) => i.id);
  const oldIdx = items.findIndex((i) => i.id === activeId);
  const newIdx = items.findIndex((i) => i.id === overId);
  if (oldIdx < 0 || newIdx < 0) return items.map((i) => i.id);
  const next = items.slice();
  const [moved] = next.splice(oldIdx, 1);
  if (!moved) return items.map((i) => i.id);
  next.splice(newIdx, 0, moved);
  return next.map((i) => i.id);
}

interface DragItemProps<T extends { id: string }> {
  item: T;
  children: (item: T) => ReactNode;
}

function DragItem<T extends { id: string }>({ item, children }: DragItemProps<T>) {
  const { attributes, isDragging, listeners, setNodeRef, transform, transition } = useSortable({
    id: item.id,
  });
  return (
    <div
      ref={setNodeRef}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.6 : 1,
      }}
      {...attributes}
      {...listeners}
    >
      {children(item)}
    </div>
  );
}

export interface DragListProps<T extends { id: string }> {
  items: T[];
  onReorder: (ids: string[]) => void;
  children: (item: T) => ReactNode;
  direction?: 'vertical' | 'horizontal';
  /** Render a static, non-draggable list (no DndContext, onReorder never fires). */
  disabled?: boolean;
}

export function DragList<T extends { id: string }>({
  items,
  onReorder,
  children,
  direction = 'vertical',
  disabled = false,
}: DragListProps<T>) {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
  );

  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event;
    if (!over) return;
    const next = reorderForDragEnd(items, String(active.id), String(over.id));
    onReorder(next);
  }

  if (disabled) {
    return (
      <>
        {items.map((item) => (
          <div key={item.id}>{children(item)}</div>
        ))}
      </>
    );
  }

  return (
    <DndContext sensors={sensors} onDragEnd={handleDragEnd}>
      <SortableContext
        items={items.map((i) => i.id)}
        strategy={
          direction === 'horizontal' ? horizontalListSortingStrategy : verticalListSortingStrategy
        }
      >
        {items.map((item) => (
          <DragItem key={item.id} item={item}>
            {children}
          </DragItem>
        ))}
      </SortableContext>
    </DndContext>
  );
}
